import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import LoginForm from "@/components/auth/LoginForm";
import { Building2, Users, Shield } from "lucide-react";

const Auth = () => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<'admin' | 'company' | 'employee' | null>(null);

  const roles = [
    {
      id: 'admin' as const,
      icon: Shield,
      titleKey: "auth.roles.admin.title",
      descriptionKey: "auth.roles.admin.description"
    },
    {
      id: 'company' as const,
      icon: Building2,
      titleKey: "auth.roles.company.title",
      descriptionKey: "auth.roles.company.description"
    },
    {
      id: 'employee' as const,
      icon: Users,
      titleKey: "auth.roles.employee.title",
      descriptionKey: "auth.roles.employee.description"
    }
  ];

  if (!selectedRole) {
    return (
      <div className="min-h-screen bg-gradient-subtle">
        <Header />
        <div className="container mx-auto py-16 px-4">
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">{t('auth.chooseProfile')}</CardTitle>
              <CardDescription>{t('auth.chooseProfileDesc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {roles.map((role) => (
                <Button
                  key={role.id}
                  variant="outline"
                  className="w-full justify-start p-4 sm:p-6 h-auto min-h-[80px]"
                  onClick={() => setSelectedRole(role.id)}
                >
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4 w-full">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <role.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="font-semibold text-sm sm:text-base">{t(role.titleKey)}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground whitespace-normal break-words">{t(role.descriptionKey)}</div>
                    </div>
                  </div>
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Header />
      <div className="container mx-auto py-16 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <Button 
              variant="ghost" 
              onClick={() => setSelectedRole(null)}
              className="mb-4"
            >
              ← {t('common.back')}
            </Button>
            <h1 className="text-2xl font-bold">{t('auth.loginTitle')} {t(`auth.roles.${selectedRole}.title`)}</h1>
            <p className="text-muted-foreground">
              {t('auth.loginToSpace')} {t(`auth.roles.${selectedRole}.title`).toLowerCase()}
            </p>
          </div>
          
          <LoginForm role={selectedRole} />
        </div>
      </div>
    </div>
  );
};

export default Auth;
